/**
 * Preset library for the computed-tags editor.
 *
 * Each preset is a starter recipe for a common computed-tag pattern:
 *   - Pt100 linearization (RTD compensation)
 *   - K-type thermocouple with cold-junction compensation
 *   - OEE = Availability × Performance × Quality
 *   - Carbon equivalent (foundry chemistry composite)
 *   - Dew point (Magnus formula)
 *   - Energy from power × time
 *
 * The TS-side registry below drives the editor's "Start from preset"
 * row today. The canonical source lives in YAML under
 * `packs/foundry/computed-tags/*.yaml` — Phase 1 wires a Vite
 * import-glob loader to read from there directly, then this registry
 * goes away.
 */

import type { DataKind } from "@foundry/foundry-plant";
import type { ComputedTagInput, ComputedTagTrigger } from "./types";

export interface ComputedTagPreset {
  id: string;
  /** Display name shown on the preset tile. */
  name: string;
  /** One-line description on the tile. */
  blurb: string;
  /** What the preset computes (industry context). */
  description: string;
  /** Suggested output unit. */
  outputUnit?: string;
  /** Suggested data_kind for the output. */
  outputDataKind: DataKind;
  /**
   * Slots the user fills in on apply. The editor pre-creates input rows
   * with the alias preset; the user picks the tag id from the catalog.
   * If `suggestedTagId` is present, the editor pre-selects it when the
   * tag exists in the catalog (best-effort match).
   */
  inputs: Array<{
    alias: string;
    description: string;
    suggestedTagId?: string;
  }>;
  /** The expression — references the aliases above. */
  expression: string;
  /** Default trigger. */
  trigger: ComputedTagTrigger;
  /** Foundry-flavoured tags / categories for filtering / future search. */
  tags: string[];
}

export const PRESETS: ComputedTagPreset[] = [
  {
    id: "pt100-linearization",
    name: "Pt100 RTD linearization",
    blurb: "Convert raw resistance to temperature",
    description:
      "Callendar-Van Dusen 2nd-order linearization for a Pt100 RTD. Inputs the raw resistance (Ω) and outputs temperature (°C) with sub-° accuracy across 0–650 °C.",
    outputUnit: "°C",
    outputDataKind: "analog_scalar",
    inputs: [
      {
        alias: "R",
        description: "Raw resistance of the Pt100 sensor.",
        suggestedTagId: "tag-rtd-resistance",
      },
    ],
    expression:
      "(-3.9083e-3 + sqrt(1.52749e-5 - 4 * -5.775e-7 * (1 - R / 100))) / (2 * -5.775e-7)",
    trigger: { kind: "on_input_change" },
    tags: ["sensor", "linearization", "RTD"],
  },
  {
    id: "k-thermocouple-compensated",
    name: "K-thermocouple cold-junction comp.",
    blurb: "Compensated bath temperature reading",
    description:
      "Linear cold-junction compensation on a K-type thermocouple's raw reading. Subtracts a fraction of the bath-to-ambient delta to remove cable-driven drift.",
    outputUnit: "°C",
    outputDataKind: "analog_scalar",
    inputs: [
      {
        alias: "T_raw",
        description: "Raw thermocouple reading.",
        suggestedTagId: "tag-furn1-bath-temp",
      },
      {
        alias: "T_amb",
        description: "Ambient (cold junction) temperature.",
        suggestedTagId: "tag-furn1-ambient",
      },
    ],
    expression: "T_raw - 0.024 * (T_raw - T_amb)",
    trigger: { kind: "on_input_change", debounceMs: 250 },
    tags: ["sensor", "compensation", "thermocouple"],
  },
  {
    id: "oee",
    name: "OEE = A × P × Q",
    blurb: "Overall Equipment Effectiveness",
    description:
      "Composite manufacturing KPI. Availability (uptime) × Performance (speed) × Quality (yield), each as a percentage. Returns a single number 0–100.",
    outputUnit: "%",
    outputDataKind: "derived_kpi",
    inputs: [
      { alias: "A", description: "Availability %", suggestedTagId: "tag-availability" },
      { alias: "P", description: "Performance %", suggestedTagId: "tag-performance" },
      { alias: "Q", description: "Quality %", suggestedTagId: "tag-quality" },
    ],
    expression: "(A * P * Q) / 10000",
    trigger: { kind: "scheduled", periodSeconds: 30 },
    tags: ["kpi", "manufacturing"],
  },
  {
    id: "carbon-equivalent",
    name: "Carbon equivalent (CE)",
    blurb: "C + (Si + P) / 3",
    // WS-E: this used to sell CE as a physics PREDICTION — as forecasting the
    // iron's freezing mode. It is arithmetic over three measured chemistry
    // values: it computes a composite, it does not solve how the metal freezes.
    // The eutectic reference point is kept because it is what the number is
    // read against; the prediction claim is not.
    //
    // Phrased around the barred wording rather than quoting it: the claim gate
    // (`physicsClaimPhrases`, connectivity_physics_test.go) scans this file
    // comments included, and a comment carrying the sentence verbatim is one
    // copy-paste from being a description string again.
    description:
      "Foundry chemistry composite computed from measured C, Si and P — the ASTM A247 composition index, read against the 4.3% eutectic point. A computed metric, not a solidification prediction.",
    outputUnit: "%",
    outputDataKind: "derived_kpi",
    inputs: [
      { alias: "C", description: "Carbon %", suggestedTagId: "tag-chem-carbon" },
      { alias: "Si", description: "Silicon %", suggestedTagId: "tag-chem-silicon" },
      { alias: "P", description: "Phosphorus %", suggestedTagId: "tag-chem-phosphorus" },
    ],
    expression: "C + (Si + P) / 3",
    trigger: { kind: "on_input_change" },
    tags: ["foundry", "chemistry", "kpi"],
  },
  {
    id: "dew-point",
    name: "Dew point (Magnus)",
    blurb: "From temperature + relative humidity",
    description:
      "Magnus-Tetens approximation. Inputs ambient T (°C) and RH (%), outputs dew point in °C. Useful for sand-system humidity control and condensation alarms.",
    outputUnit: "°C",
    outputDataKind: "analog_scalar",
    inputs: [
      { alias: "T", description: "Ambient temperature °C", suggestedTagId: "tag-air-temp" },
      { alias: "RH", description: "Relative humidity %", suggestedTagId: "tag-air-rh" },
    ],
    expression:
      "(243.04 * (log(RH / 100) + (17.625 * T) / (243.04 + T))) / (17.625 - log(RH / 100) - (17.625 * T) / (243.04 + T))",
    trigger: { kind: "scheduled", periodSeconds: 60 },
    tags: ["environment", "calc"],
  },
  {
    id: "energy-kwh",
    name: "Energy = power × time",
    blurb: "Trapezoidal kWh accumulation",
    description:
      "Integrate a power reading (kW) over the polling interval to accumulate energy (kWh). Phase 0 estimates the interval from `lag()`; Phase 1 swaps in the real sample timestamp from the broker.",
    outputUnit: "kWh",
    outputDataKind: "count_scalar",
    inputs: [
      {
        alias: "P_kw",
        description: "Power reading in kW",
        suggestedTagId: "tag-furn1-power-kw",
      },
    ],
    // EMA smooths the power then divides by 3600 / dt — for the demo,
    // a coarse 1 s dt is fine; the value increments steadily.
    expression: "rolling_mean(P_kw, 60) / 3600",
    trigger: { kind: "scheduled", periodSeconds: 1 },
    tags: ["energy", "preview"],
  },
  {
    id: "gfn",
    name: "GFN = Σ(multᵢ·massᵢ) / Σmassᵢ",
    blurb: "AFS Grain Fineness Number",
    description:
      "AFS grain fineness (AFS 1106) of a green-sand sample: a weighted average of the mass retained on each standard sieve. Bind the 12 per-mesh retained-mass tags (grams) from a sieve shaker (#6 → pan); returns a single AFS number.",
    outputUnit: "AFS",
    outputDataKind: "derived_kpi",
    inputs: [
      { alias: "m6", description: "Retained mass on ASTM #6 (3350 µm)" },
      { alias: "m12", description: "Retained mass on ASTM #12 (1700 µm)" },
      { alias: "m20", description: "Retained mass on ASTM #20 (850 µm)" },
      { alias: "m30", description: "Retained mass on ASTM #30 (600 µm)" },
      { alias: "m40", description: "Retained mass on ASTM #40 (425 µm)" },
      { alias: "m50", description: "Retained mass on ASTM #50 (300 µm)" },
      { alias: "m70", description: "Retained mass on ASTM #70 (212 µm)" },
      { alias: "m100", description: "Retained mass on ASTM #100 (150 µm)" },
      { alias: "m140", description: "Retained mass on ASTM #140 (106 µm)" },
      { alias: "m200", description: "Retained mass on ASTM #200 (75 µm)" },
      { alias: "m270", description: "Retained mass on ASTM #270 (53 µm)" },
      { alias: "mpan", description: "Retained mass in the pan (< 53 µm)" },
    ],
    expression:
      "(3*m6 + 5*m12 + 10*m20 + 20*m30 + 30*m40 + 40*m50 + 50*m70 + 70*m100 + 100*m140 + 140*m200 + 200*m270 + 300*mpan) / (m6 + m12 + m20 + m30 + m40 + m50 + m70 + m100 + m140 + m200 + m270 + mpan)",
    trigger: { kind: "on_input_change" },
    tags: ["qc", "sand", "foundry"],
  },
];

/**
 * Look up a preset by id. Used by the editor when the user picks a tile.
 */
export function getPreset(id: string): ComputedTagPreset | undefined {
  return PRESETS.find((p) => p.id === id);
}

/**
 * Turn a preset into a partial draft the editor can hydrate.
 */
export function presetToDraft(
  preset: ComputedTagPreset,
  catalog: Array<{ id: string }>,
): {
  name: string;
  description: string;
  outputUnit: string;
  outputDataKind: DataKind;
  inputs: ComputedTagInput[];
  expression: string;
  trigger: ComputedTagTrigger;
  presetId: string;
} {
  const inputs: ComputedTagInput[] = preset.inputs.map((p) => {
    const suggested = p.suggestedTagId && catalog.find((c) => c.id === p.suggestedTagId);
    return {
      tagId: suggested ? p.suggestedTagId! : "",
      alias: p.alias,
    };
  });
  return {
    name: preset.name,
    description: preset.description,
    outputUnit: preset.outputUnit ?? "",
    outputDataKind: preset.outputDataKind,
    inputs,
    expression: preset.expression,
    trigger: preset.trigger,
    presetId: preset.id,
  };
}
